import { h, Component } from 'preact';
import * as THREE from 'three';
import s from 'components/bolt/upWorld/upWorld.sss';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import ShootingStars from 'components/bolt/upWorld/shootingStars.js';
import BackgroundStars from './backgroundStars.js';
import Planet from './planet.js';
//import Starships from './starships.js';
import EasterEgg from './easterEgg';
import { joinClasses } from 'utils/utils.js';
import Store from 'store/store';
//import { observeScroll, unobserveScroll } from 'utils/scrollObserver';

export default class UpWorld extends Component {

	constructor(props) {
		super(props);

		this.props = props;
		const { imageRoot, videoRoot } = this.props;
		this.imageRoot = imageRoot;
		this.videoRoot = videoRoot;
		this.scene;
		this.composer;
		this.camera;
		this.shootingStars;
		this.backgroundStars;
		this.raycaster;
		this.mouse;
		this.hoverOnStar = false;

		this.state = {
			easterEggOpen: false,
			easterEggNum: 0
		};
		
		this.initScene = this.initScene.bind(this);
		this.addLights = this.addLights.bind(this);
		this.resize = this.resize.bind(this);
		this.animate = this.animate.bind(this);
		this.addBloomPass = this.addBloomPass.bind(this);
		this.getVisibleWidthAtDepth = this.getVisibleWidthAtDepth.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onMouseMove = this.onMouseMove.bind(this);
		this.toggleEasterEgg = this.toggleEasterEgg.bind(this);
		this.setEasterEgg = this.setEasterEgg.bind(this);
	}

	componentDidMount() {
		window.addEventListener('resize', this.resize);
		this.$scene.addEventListener('click', (event) => { 
			this.onClick(event);
		});
		this.$scene.addEventListener('mousemove', (event) => { 
			this.onMouseMove(event);
		});
		this.initScene();
		//observeScroll(this.base, { once: true });
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.resize);
		this.$scene.removeEventListener('click', this.onClick);
		this.$scene.removeEventListener('click', this.onMouseMove);
		//unobserveScroll(this.base);
	}

	resize() {
		this.camera.aspect = this.$scene.clientWidth / this.$scene.clientHeight;
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(this.$scene.clientWidth, this.$scene.clientHeight);
		this.renderer.setPixelRatio(window.devicePixelRatio);
		const windowWidth = this.getVisibleWidthAtDepth();
		this.backgroundStars.resize(windowWidth);
		this.shootingStars.resize(windowWidth);
	}

	initScene() {
		this.scene = new THREE.Scene();
		this.scene.background = null;

		this.camera = new THREE.PerspectiveCamera(45, this.$scene.clientWidth / this.$scene.clientHeight, 0.1, 2000 );
		this.camera.position.z = 10;

		this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, premultipliedAlpha: true });
		this.renderer.toneMapping = THREE.ReinhardToneMapping;
		this.renderer.toneMappingExposure = 1.5;
		this.renderer.setClearColor(0xffffff, 0);
		this.renderer.outputEncoding = THREE.sRGBEncoding;
		this.renderer.physicallyCorrectLights = true;
		this.$scene.appendChild(this.renderer.domElement);
		
		this.clock = new THREE.Clock();
		this.windowWidth = this.getVisibleWidthAtDepth();

		this.shootingStars = new ShootingStars ({
			scene: this.scene,
			imageRoot: this.imageRoot,
		});

		this.shootingStars.init().catch(console.error);

		this.backgroundStars = new BackgroundStars ({
			scene: this.scene,
			imageRoot: this.imageRoot,
		});
		this.backgroundStars.init().catch(console.error);

		this.raycaster = new THREE.Raycaster();
		this.mouse = new THREE.Vector2();

		this.addBloomPass();
		this.addLights();
		this.animate();	
		this.resize();
	}

	addBloomPass() {
		const renderTarget = new THREE.WebGLRenderTarget(
			this.$scene.clientWidth, this.$scene.clientHeight,
			{ format: THREE.RGBAFormat }
		);

		this.composer = new EffectComposer(this.renderer, renderTarget);

		const renderPass = new RenderPass(this.scene, this.camera);
		renderPass.clearAlpha = 0;
		this.composer.addPass(renderPass);

		const bloomPass = new UnrealBloomPass(
			new THREE.Vector2(this.$scene.clientWidth, this.$scene.clientHeight),
			0.5, 0.5, 0.1
		);
		this.composer.addPass(bloomPass);

		const finalPass = new ShaderPass(
			new THREE.ShaderMaterial({
				uniforms: {
					baseTexture: { value: null },
					bloomTexture: { value: this.composer.renderTarget2.texture }
				},
				vertexShader: `
					varying vec2 vUv;
					void main() {
						vUv = uv;
						gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
					}`,
				fragmentShader: `
					uniform sampler2D baseTexture;
					uniform sampler2D bloomTexture;
					varying vec2 vUv;
					void main() {
						vec4 base = texture2D(baseTexture, vUv);
						vec4 bloom = texture2D(bloomTexture, vUv);
						gl_FragColor = vec4(base.rgb + bloom.rgb, max(base.a, bloom.r));
					}`,
				transparent: true
			}), 'baseTexture'
		);
		this.composer.addPass(finalPass);
	}

	addLights() {
		const light = new THREE.AmbientLight(0xffffff, 0.2);
		this.scene.add(light);

		const pointLight = new THREE.PointLight(0xffffff, 1);
		pointLight.power = 2000;
		pointLight.position.set(0, 0, 5);
		this.scene.add(pointLight);
	}

	animate() {
		const { updateUpWorld, downWorldFound } = this.props;
		this.animationFrame = window.requestAnimationFrame(this.animate);
		//this.renderer.render(this.scene, this.camera);

		if (downWorldFound) return;

		const time = this.clock.getElapsedTime();
		
		if (updateUpWorld) {
			this.shootingStars.updateStars(time);
		}

		this.backgroundStars.updateStars(time);

		if (this.composer != undefined) {
			this.composer.render();
		}
	}	

	getVisibleWidthAtDepth() {
		const distance = Math.abs(this.camera.position.z);
		const vFOV = this.camera.fov * Math.PI / 180;
		const visibleHeight = 2 * Math.tan(vFOV / 2) * distance;
		const visibleWidth = visibleHeight * this.camera.aspect;
		return visibleWidth;
	}

	onClick(event) {
		const { easterEggOpen } = this.state;
		if (!easterEggOpen) {
			this.mouse.x = ((event.clientX / this.$scene.clientWidth) * 2) - 1;
			this.mouse.y = - ((event.clientY / this.$scene.clientHeight) * 2) + 1;

			this.raycaster.setFromCamera(this.mouse, this.camera);

			const intersects = this.raycaster.intersectObjects(this.scene.children, true);
			if (intersects.length > 0) {
				let hadClickedOnStar = false;
				intersects.forEach(i => {
					if (i.object.name.includes('shootingStar') && !hadClickedOnStar) {
						let starSound = 'starTrail' + Math.floor(Math.random() * 4);
						Store.emit('PLAY_SOUND', starSound);

						let starNum = i.object.name.replace(/\D/g, '');
						hadClickedOnStar = true;
						if (starNum == 2) {
							Store.emit('CUSTOM_EVENT', {
								category: 'up-world',
								action: 'star-shower-click',
							});

							this.setEasterEgg(0);
						}
					}
				});
			}
		}
	}

	onMouseMove(event) {
		const { easterEggOpen } = this.state;
		if (!easterEggOpen ) {
			this.mouse.x = ((event.clientX / this.$scene.clientWidth) * 2) - 1;
			this.mouse.y = - ((event.clientY / this.$scene.clientHeight) * 2) + 1;

			this.raycaster.setFromCamera(this.mouse, this.camera);

			const intersects = this.raycaster.intersectObjects(this.scene.children, true);
			if (intersects.length > 0) {
				intersects.forEach(i => {
					if (i.object.name.includes('shootingStar')) {
						if (!this.hoverOnStar) {
							this.hoverOnStar = true;
							Store.emit('HOVER_ON_STAR', true);
						}
					}
				});
			} else {				
				if (this.hoverOnStar) {
					this.hoverOnStar = false;
					Store.emit('HOVER_ON_STAR', false);
				}
			}
		}
	}

	setEasterEgg(easterEggNum) {
		this.setState({ easterEggNum: easterEggNum });
		if (!this.state.easterEggOpen) {
			this.toggleEasterEgg();
		}
	}

	toggleEasterEgg() {
		this.setState({ easterEggOpen: !this.state.easterEggOpen });
	}

	render() {
		const { downWorldFound, copy } = this.props;
		const { easterEggOpen, easterEggNum } = this.state;
		return (
			<div class={joinClasses(s.container, downWorldFound && s.hide)} id ="upWorld-container">
				{/* <canvas ref={canvas => this.$canvas = canvas} class={s.starCanvas} /> */}
				<Planet imageRoot={this.imageRoot} copy={copy} />
				{/* <Starships imageRoot={this.imageRoot} copy={copy} setEasterEgg={(easterEggNum) => this.setEasterEgg(easterEggNum)} /> */}
				<div ref={ref => this.$scene = ref} class={s.scene} />
				<EasterEgg imageRoot={this.imageRoot} videoRoot={this.videoRoot} copy={copy} isOpen={easterEggOpen} easterEggNum={easterEggNum} onClose={easterEggOpen && this.toggleEasterEgg} />
			</div>
		);
	}
}